/**
 * AddUserModal: invite a person to an organization by email. A Platform Admin
 * types the address, picks a role and the tenants they may see, and the server
 * finds the account in the organization's Entra directory. The person sees
 * their tenants the first time they sign in. A Carbon Modal (layer-02, pinned
 * header, the footer button set).
 *
 * Opened from the organization drawer's "Invite admin" action. The Modal is
 * a headless UI Dialog that portals itself, so it renders above the drawer
 * and a click inside it never reaches the drawer underneath.
 *
 * When the directory lookup can't settle on one account, the "Enter their
 * object ID instead" disclosure opens and a resubmit carries the object ID.
 */
import React, { useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import type { InviteUserPayload, InviteUserResponse } from '@/lib/api/admin'
import type { Tenant } from '@/types'
import { Modal } from '@/components/carbon/Modal'
import { Checkbox } from '@/components/carbon/Checkbox'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Select } from '@/components/carbon/Select'
import { TextInput } from '@/components/carbon/TextInput'

export interface InviteOrganization {
  id: string
  name: string
  type: string
  azureAdTenantId?: string | null
}

interface AddUserModalProps {
  isOpen: boolean
  organization: InviteOrganization
  onClose: () => void
  onUserAdded?: () => void
}

export interface InviteFormState {
  email: string
  displayName: string
  role: string
  tenantIds: string[]
  sendEmail: boolean
  objectId: string
}

const ROLES = [
  { value: 'Viewer', label: 'Viewer', description: 'Reads dashboards and changes nothing.' },
  { value: 'Approver', label: 'Approver', description: 'Accepts or rejects staged drift, plus everything a Viewer can do.' },
  { value: 'ComplianceOfficer', label: 'Compliance Officer', description: 'Reads compliance reports and exports data.' },
  { value: 'SecurityAdmin', label: 'Security Admin', description: 'Manages policies and reads all data.' },
  { value: 'GlobalAdmin', label: 'Global Admin', description: 'Full access, including tenant management and user provisioning.' }
]

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Error codes that open the object-ID fallback on their own. not_found_in_directory
 * stays out: an object ID copied from another tenant would make a dead invite.
 */
const FALLBACK_CODES = ['directory_unavailable', 'ambiguous_directory_match']

/** Points at the organization's own directory: an object ID from another tenant makes a dead invite. */
export function objectIdHelp(orgName: string): string {
  return `In ${orgName}'s Microsoft Entra ID, open Users, select the person and copy the Object ID.`
}

export type TenantLoadStatus = 'loading' | 'loaded' | 'failed'

/** Submit waits for the tenant list: without it the invitee could land with nothing to see. */
export function submitBlocked(missingTid: boolean, tenantStatus: TenantLoadStatus): boolean {
  return missingTid || tenantStatus !== 'loaded'
}

export function opensFallback(code: string | undefined): boolean {
  return !!code && FALLBACK_CODES.includes(code)
}

const EMPTY_FORM: InviteFormState = {
  email: '',
  displayName: '',
  role: 'SecurityAdmin',
  tenantIds: [],
  sendEmail: true,
  objectId: ''
}

/** GlobalAdmin is offered only inside the Platform organization. */
export function rolesFor(orgType: string) {
  return orgType === 'Platform' ? ROLES : ROLES.filter(r => r.value !== 'GlobalAdmin')
}

/**
 * The tenants to pre-check. Tenants carry no organization link on the client,
 * so this is the tenant that matches the organization's Entra tenant ID.
 */
export function defaultTenantIds(organization: InviteOrganization, tenants: Tenant[]): string[] {
  const tid = organization.azureAdTenantId?.trim().toLowerCase()
  if (!tid) return []
  return tenants.filter(t => t.tenantId.toLowerCase() === tid).map(t => t.tenantId)
}

export function buildInvitePayload(form: InviteFormState, organization: InviteOrganization): InviteUserPayload {
  const objectId = form.objectId.trim()
  const displayName = form.displayName.trim()
  return {
    email: form.email.trim(),
    organizationId: organization.id,
    role: form.role,
    tenantIds: form.tenantIds,
    sendEmail: form.sendEmail,
    ...(displayName ? { displayName } : {}),
    ...(objectId ? { objectId } : {})
  }
}

export function validateInviteForm(form: InviteFormState, fallbackOpen: boolean): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!form.email.trim()) {
    errors.email = 'Email is required.'
  } else if (!EMAIL_REGEX.test(form.email.trim())) {
    errors.email = 'Enter a valid email address.'
  }
  const objectId = form.objectId.trim()
  if (fallbackOpen && objectId && !GUID_REGEX.test(objectId)) {
    errors.objectId = 'Enter a GUID in the form xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx.'
  }
  return errors
}

export function inviteButtonLabel(sendEmail: boolean): string {
  return sendEmail ? 'Send invite' : 'Invite'
}

/** The toast text for a finished invite; `warn` when an email was asked for and didn't go. */
export function inviteToast(
  response: InviteUserResponse,
  form: InviteFormState,
  organization: InviteOrganization
): { message: string; warn: boolean } {
  const email = form.email.trim()
  if (form.sendEmail && !response.emailSent) {
    return { message: `Invited ${email}, but the email didn't send. Send it from People.`, warn: true }
  }
  return { message: `Invited ${email}. They see ${organization.name} on first sign-in.`, warn: false }
}

export default function AddUserModal({ isOpen, organization, onClose, onUserAdded }: AddUserModalProps) {
  const formId = useId()
  const [form, setForm] = useState<InviteFormState>(EMPTY_FORM)
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([])
  const [tenantStatus, setTenantStatus] = useState<TenantLoadStatus>('loading')
  const [showFallback, setShowFallback] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const missingTid = !organization.azureAdTenantId?.trim()

  // Load available tenants when the modal opens, pre-checking the organization's own.
  useEffect(() => {
    if (!isOpen) return
    setForm(EMPTY_FORM)
    setTenantStatus('loading')
    setAvailableTenants([])
    setShowFallback(false)
    setError(null)
    setValidationErrors({})
    let cancelled = false
    api.getTenants()
      .then(tenants => {
        if (cancelled) return
        setAvailableTenants(tenants)
        setTenantStatus('loaded')
        setForm(prev => ({ ...prev, tenantIds: defaultTenantIds(organization, tenants) }))
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load tenants:', err)
        setTenantStatus('failed')
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, organization.id, organization.azureAdTenantId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitBlocked(missingTid, tenantStatus)) return

    const errors = validateInviteForm(form, showFallback)
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) return

    try {
      setLoading(true)
      setError(null)

      const response = await api.inviteUser(buildInvitePayload(form, organization))

      const { message, warn } = inviteToast(response, form, organization)
      if (warn) toast(message)
      else toast.success(message)
      onUserAdded?.()
      onClose()
    } catch (err: unknown) {
      console.error('Failed to invite user:', err)
      const body = api.getInviteError(err)
      if (opensFallback(body?.code)) setShowFallback(true)
      setError(body?.message ?? 'Check the address and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleTenantToggle = (tenantId: string) => {
    setForm(prev => ({
      ...prev,
      tenantIds: prev.tenantIds.includes(tenantId)
        ? prev.tenantIds.filter(id => id !== tenantId)
        : [...prev.tenantIds, tenantId]
    }))
  }

  const roles = rolesFor(organization.type)
  const role = roles.find(r => r.value === form.role)
  const tenantsLoading = tenantStatus === 'loading'

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      title={`Invite to ${organization.name}`}
      subtitle={`They see their tenants the first time they sign in with their ${organization.name} account.`}
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: loading }}
      primaryAction={{
        label: inviteButtonLabel(form.sendEmail),
        type: 'submit',
        form: formId,
        loading,
        disabled: submitBlocked(missingTid, tenantStatus)
      }}
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        {missingTid && (
          <InlineNotification kind="info" title="No Entra tenant ID.">
            {`${organization.name} has no Entra tenant ID, so Guardian can't tell where its people sign in.`}
          </InlineNotification>
        )}

        {tenantStatus === 'failed' && (
          <InlineNotification kind="error" title="Tenants didn't load.">
            Close and reopen this dialog.
          </InlineNotification>
        )}

        {error && (
          <InlineNotification kind="error" title="The invite didn't go through.">
            {error}
          </InlineNotification>
        )}

        <TextInput
          id="email"
          type="email"
          label="Email"
          value={form.email}
          onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
          placeholder="user@company.com"
          error={validationErrors.email}
          disabled={loading}
          required
        />

        <TextInput
          id="displayName"
          label="Display name (optional)"
          value={form.displayName}
          onChange={(e) => setForm(prev => ({ ...prev, displayName: e.target.value }))}
          placeholder="Jane Doe"
          helper="Guardian uses their directory name when this is blank."
          disabled={loading}
        />

        <Select
          id="role"
          label="Role"
          value={form.role}
          onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
          options={roles.map(r => ({ value: r.value, label: r.label }))}
          helper={role?.description}
          disabled={loading}
        />

        <fieldset className="m-0 min-w-0 border-0 p-0">
          <legend className="mb-2 p-0 text-helper text-text-secondary">Tenant access (optional)</legend>
          <div className="max-h-48 overflow-y-auto border-b border-line-strong bg-field px-4 py-3">
            {tenantsLoading ? (
              <p className="m-0 text-body text-text-helper">Loading tenants</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {availableTenants.map(tenant => (
                  <li key={tenant.tenantId}>
                    <Checkbox
                      checked={form.tenantIds.includes(tenant.tenantId)}
                      onChange={() => handleTenantToggle(tenant.tenantId)}
                      disabled={loading}
                      label={
                        <>
                          {tenant.name || tenant.tenantId}
                          {tenant.domain && <span className="ml-2 text-text-helper">({tenant.domain})</span>}
                        </>
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="m-0 mt-1 text-helper text-text-helper">
            The tenants they can see. You can assign more later from the People tab.
          </p>
        </fieldset>

        <div className="flex flex-col gap-1">
          <Checkbox
            checked={form.sendEmail}
            onChange={(e) => setForm(prev => ({ ...prev, sendEmail: e.target.checked }))}
            disabled={loading}
            label="Send the invite email"
          />
          <p className="m-0 pl-6 text-helper text-text-helper">
            It links to Guardian and says which account to sign in with.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => setShowFallback(open => !open)}
            aria-expanded={showFallback}
            className="m-0 w-fit border-0 bg-transparent p-0 text-body text-primary underline"
          >
            Enter their object ID instead
          </button>
          {showFallback && (
            <>
              <InlineNotification kind="info" title="Find the Entra object ID.">
                {objectIdHelp(organization.name)}
              </InlineNotification>
              <TextInput
                id="objectId"
                label="Entra object ID"
                value={form.objectId}
                onChange={(e) => setForm(prev => ({ ...prev, objectId: e.target.value }))}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                error={validationErrors.objectId}
                disabled={loading}
                className="font-mono"
              />
            </>
          )}
        </div>
      </form>
    </Modal>
  )
}
